import { DateLabel } from "components-redesign/DateLabel/DateLabel";
import { microToDollarsString } from "helpers/metrics";
import { bytesToFileSize } from "helpers/string";
import { getFilenameFromUrl } from "helpers/url";
import { NETWORK_ANONYMITY_LABELS } from "model/lineitems";
import { ArrayValue } from "./ArrayValue";
import { AspectRatioName } from "./AspectRatioName";
import { LanguageField } from "./LanguageField";
import { LocationName } from "./LocationName";

type FieldValueRenderer = (value: string, fieldPath: string) => React.ReactNode;

const FIELD_RENDERERS: Array<{
  pattern: RegExp;
  renderer: FieldValueRenderer;
}> = [
  {
    pattern: /language/,
    renderer: (value: string) => <LanguageField value={value} />,
  },
  {
    pattern: /networkAnonymity\/anonymityLevelsAllowed/,
    renderer: (value: string) => {
      let levels: unknown;
      try {
        levels = JSON.parse(value);
      } catch {
        return null;
      }

      // An empty selection falls through to the generic renderer.
      if (!Array.isArray(levels) || !levels.length) {
        return null;
      }

      return (
        <span>
          {levels
            .map((level) => NETWORK_ANONYMITY_LABELS[level] ?? level)
            .join(", ")}
        </span>
      );
    },
  },
  {
    pattern: /./,
    renderer: (value: string, fieldPath: string) => {
      if (value.startsWith("[") && value.endsWith("]")) {
        return (
          <ArrayValue
            value={value}
            fieldPath={fieldPath}
          />
        );
      }
      return null;
    },
  },
  {
    pattern: /geoCriteria\/(whitelist|blacklist)/,
    renderer: (value: string) => <LocationName locationId={value} />,
  },
  {
    pattern: /aspectRatios\/values/,
    renderer: (value: string) => <AspectRatioName value={value} />,
  },
  {
    pattern: /\/creative\/assets\/\d+\/image\/url$/,
    renderer: (value: string) => getFilenameFromUrl(value),
  },
  {
    pattern: /\/creative\/contentLength$/,
    renderer: (value: string) => {
      const bytes = parseInt(value, 10);
      return isNaN(bytes) ? value : bytesToFileSize(bytes);
    },
  },
  {
    pattern: /(\/start|\/finish|\/endDate)$/,
    renderer: (value: string) => (
      <DateLabel
        date={value}
        mask="dd/mm/yyyy hour:min"
        utc
      />
    ),
  },
  {
    pattern:
      /\/(budget|bid|maxCpm)$|\/cap\/value$|\/exchangeCpm\/|\/revenuePerInstall$|\/dl4j\/(cpmMax|userValue)$|\/xgboost\/(cpmMax|userValue)$|\/xgboost\/params\/(targetCPI|targetCPA)$|\/ext\/(publisherIdCPM|tagIdCPM)\/[^/]+$/,
    renderer: (value: string) => {
      const microValue = parseFloat(value);
      if (isNaN(microValue)) return value;
      return microToDollarsString(microValue as any);
    },
  },
];

export const getComponentForFieldValue = (
  fieldName: string,
  value: string
): React.ReactNode => {
  for (const { pattern, renderer } of FIELD_RENDERERS) {
    if (pattern.test(fieldName)) {
      const result = renderer(value, fieldName);
      if (result !== null) {
        return result;
      }
    }
  }

  return value;
};
